import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppDispatch, RootState } from '../index';
import { FetchState } from '../fetchState';
import { storageResetAction } from '../storeReset';
import { AnalyticsResponseDto } from '@api/models/analytics/AnalyticsResponseDto';

export interface AnalyticsEntry {
  state: FetchState;
  data: AnalyticsResponseDto | undefined;
}

export interface AnalyticsState {
  entries: Record<string, AnalyticsEntry>;
}

const initialState: AnalyticsState = {
  entries: {},
};

const newEntry = (): AnalyticsEntry => ({
  state: FetchState.None,
  data: undefined,
});

export const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    request(state, action: PayloadAction<string>) {
      const key = action.payload;
      state.entries[key] = { ...(state.entries[key] ?? newEntry()), state: FetchState.Loading };
    },
    success(state, action: PayloadAction<{ key: string; data: AnalyticsResponseDto }>) {
      const { key, data } = action.payload;
      state.entries[key] = { state: FetchState.Loaded, data };
    },
    error(state, action: PayloadAction<string>) {
      const key = action.payload;
      state.entries[key] = { ...(state.entries[key] ?? newEntry()), state: FetchState.Error };
    },
    reset() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(storageResetAction, () => initialState);
  },
});

export const analyticsActions = analyticsSlice.actions;

const EMPTY_ENTRY: AnalyticsEntry = { state: FetchState.None, data: undefined };

export const selectAnalyticsEntry =
  (key: string) =>
  (state: RootState): AnalyticsEntry =>
    state.analytics.entries[key] ?? EMPTY_ENTRY;

export const fetchAnalytics = (key: string, fetcher: () => Promise<AnalyticsResponseDto>) => async (dispatch: AppDispatch) => {
  dispatch(analyticsActions.request(key));
  try {
    const data = await fetcher();
    dispatch(analyticsActions.success({ key, data }));
  } catch {
    dispatch(analyticsActions.error(key));
  }
};

export const resetAnalytics = () => (dispatch: AppDispatch) => {
  dispatch(analyticsActions.reset());
};
